// Copyright (c) 2025 TrustCloud Corporation - All rights reserved.
//
// This software contains valuable confidential and proprietary information of
// TrustCloud Corporation
//
// Unauthorized use, reproduction, transmission, or distribution of this file and its
// contents is a violation of applicable law.

import styled from '@emotion/styled';
import { Card, color, Table, TabRoot } from '@kintent/glide';

export const Wrapper = styled.main`
  grid-area: main;

  display: flex;
  flex-direction: column;
  gap: 24px;

  padding: 10rem 6rem 4rem 6rem;

  background-color: ${({ theme }) => color(theme.colorV2.system.gray, 100)};

  min-height: 100vh;

  h1 {
    font-weight: 600;
  }
`;

export const LeftPanel = styled(Card)`
  grid-area: left-panel;

  border-radius: 16px;
  padding: 24px;

  a {
    display: block;
    cursor: pointer;

    padding: 8px;
    border-radius: 8px;

    text-decoration: none;

    &.active {
      background: ${({ theme }) => color(theme.colorV2.system.gray, 50)};
    }
  }

  & > div {
    padding-bottom: 32px;
  }

  ul {
    list-style: none;

    display: flex;
    flex-direction: column;
    gap: 20px;

    li {
      a {
        display: flex;
        align-items: center;
        gap: 8px;
      }
    }
  }
`;

export const CertificationContentPanel = styled.section`
  display: flex;
  flex-direction: column;
  gap: 20px;

  & .certification-content-product-filter-container {
    display: flex;
    align-items: center;
    gap: 16px;

    span {
      font-weight: 500;
      font-family: Lato;
    }
  }
`;

export const CertificationGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 24px;
`;

export const CertificationCard = styled(Card)`
  display: flex;
  flex-direction: column;
  gap: 16px;
  text-decoration: none;

  & .certification-card-logo {
    height: 56px;
    width: 56px;

    img {
      height: auto;
      width: 100%;
    }
  }

  & .certification-card-title {
    font-weight: 600;
  }

  & .certification-card-description {
    display: -webkit-box;
    overflow: hidden;
    line-clamp: 3;
    -webkit-line-clamp: 3;
    -webkit-box-orient: vertical;
    font-weight: 500;
    font-family: Lato;
  }

  & .certification-card-related-products {
    margin-top: auto;
    font-weight: 500;
    font-family: Lato;

    span {
      padding-left: 8px;
    }
  }
`;

export const CertificationDetailGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;

  & .certification-detail-heading-card {
    display: flex;
    flex-direction: column;
    gap: 16px;

    & .certification-detail-heading-card-title {
      font-weight: 600;
    }

    & .certification-detail-heading-card-description {
      font-weight: 500;
      font-family: Lato;
    }

    & .certification-detail-heading-card-logo-container {
      width: 32px;
      height: 32px;

      & > img {
        max-width: 100%;
        height: auto;
        object-fit: cover;
      }
    }
  }

  & .certification-detail-mapping-card {
    display: flex;
    flex-direction: column;
    gap: 16px;

    & .certification-detail-mapping-card-title {
      font-weight: 600;

      & + p {
        padding-bottom: 20px;
      }
    }
  }
`;

export const ReportsContainer = styled(Card)`
  display: flex;
  flex-direction: column;
  gap: 36px;
`;

export const ReportsHeaderContainer = styled('div')`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;

  & > h3 {
    font-weight: 600;
  }
`;

export const ReportsTable = styled(Table)`
  border-radius: 8px;
  border: 1px solid ${({ theme }) => color(theme.colorV2.system.border, 600)};
  box-shadow: none;
  overflow: hidden;

  tr {
    grid-template-columns: 1.5fr 2.5fr 1fr 1.5fr 100px;
  }

  tr td span {
    font-weight: 500;
    font-family: Lato;
  }

  & > td {
    border: none;
  }

  & > thead > tr {
    border-left: none;
    border-right: none;
  }

  & > tr {
    border: none;
  }

  & > tbody > tr {
    border: none;
  }
`;

export const ReportsTitleWrapper = styled('div')`
  display: flex;
  align-items: center;
  gap: 8px;
`;

export const TabListContainer = styled(TabRoot)`
  font-weight: 500;
  font-family: Lato;
  & [role='tablist'] {
    & button {
      color: ${({ theme }) => color(theme.colorV2.system.gray, 600)};
      background-color: white;
      border-color: ${({ theme }) => color(theme.colorV2.system.border, 600)};
    }

    & button[data-state='active'] {
      color: black;
      background-color: ${({ theme }) => theme.components.button.background.primary};
    }

    & button[data-state='active'] + button[data-state='inactive'] {
      border-left-color: ${({ theme }) => color(theme.colorV2.system.border, 600)};
    }

    & button:hover + button[data-state='inactive'] {
      border-color: ${({ theme }) => color(theme.colorV2.system.border, 600)};
    }
  }
`;

export const MappingRow = styled(Card)`
  display: grid;
  grid-template-columns: 1fr 140px 140px 120px;
  gap: 24px;

  background-color: white;

  border-radius: 8px;
  border: 1px solid ${({ theme }) => color(theme.colorV2.system.border, 600)};

  padding: 16px 20px;
  font-weight: 500;
  font-family: Lato;

  & .certification-mapping-row-display-identifier + span {
    padding-left: 8px;
  }

  & .mapping-tag {
    display: inline-flex;
    justify-content: center;
  }
`;

export const Section = styled.section`
  display: grid;
  grid-template-areas: 'left-panel certification-content-panel';
  grid-template-columns: 260px 1fr;
  column-gap: 64px;

  height: 100%;
`;

export const CertificationContainer = styled.section`
  display: flex;
  flex-direction: column;
  gap: 36px;

  width: 1200px;

  @media (min-width: 1920px) {
    width: 1400px;
  }

  margin-left: auto;
  margin-right: auto;
`;
